import { useEffect, useRef, useState } from "react";
import { Card, Col, Container, Dropdown, DropdownButton, Form, Row, Badge } from "react-bootstrap";
import { useQuery } from "react-query";
import { Line, Pie } from "react-chartjs-2";
import { useParams } from "react-router-dom";
import { useAgentContext } from "../../context/agent";
import { getMarktingData } from "../../api/agent";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {FaChartLine, FaDownload, FaComments, FaUsers, FaGlobe, FaCalendarAlt, FaFileExport} from "react-icons/fa";
import { SiWhatsapp } from "react-icons/si";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement, } from "chart.js";
import PublishAgent from "../Deploy/PublishAgent";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

function MarketingDashboard() {
    const [chartData, setChartData] = useState<any>(null);
    const [pieChatData, setPieChatData] = useState<any>(null);

    // default date range is last 7 days
    const [dateRange, setDateRange] = useState(() => {
        const toDate = new Date();
        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 7);
        return { fromDate, toDate };
    });

    const chartRef = useRef<ChartJS<"line"> | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const { agentId } = useParams();
    const { getAgentById } = useAgentContext();
    const agent = getAgentById(agentId || "");

    const { data, isLoading } = useQuery({
        queryKey: [
            "Marketing",
            agent?.accessKey,
            dateRange.fromDate.toISOString(),
            dateRange.toDate.toISOString(),
        ],
        queryFn: () =>
            getMarktingData({
                orgId: agent?.organizationId!,
                agentId: agent?.id!,
                dateRange,
            }),
        enabled: !!agent?.id && !!agent?.organizationId,
    });

    const handleSelection = (option: string) => {
        const now = new Date();
        let fromDate, toDate;

        switch (option) {
            case "7days":
                fromDate = new Date();
                fromDate.setDate(now.getDate() - 7);
                toDate = now;
                break;
            case "30days":
                fromDate = new Date();
                fromDate.setDate(now.getDate() - 30);
                toDate = now;
                break;
            case "60days":
                fromDate = new Date();
                fromDate.setDate(now.getDate() - 60);
                toDate = now;
                break;
            case "90days":
                fromDate = new Date();
                fromDate.setDate(now.getDate() - 90);
                toDate = now;
                break;
            case "lastCalendarWeek":
                const lastWeek = new Date(now);
                const dayOfWeek = now.getDay();
                const daysSinceSunday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                lastWeek.setDate(now.getDate() - daysSinceSunday - 7);
                fromDate = lastWeek;
                toDate = new Date(lastWeek);
                toDate.setDate(toDate.getDate() + 6);
                break;
            case "lastCalendarMonth":
                fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                toDate = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case "thisYear":
                fromDate = new Date(now.getFullYear(), 0, 1);
                toDate = now;
                break;
            case "last12months":
                fromDate = new Date();
                fromDate.setFullYear(now.getFullYear() - 1);
                toDate = now;
                break;
            case "lastCalendarYear":
                fromDate = new Date(now.getFullYear() - 1, 0, 1);
                toDate = new Date(now.getFullYear() - 1, 11, 31);
                break;
            default:
                return;
        }

        setDateRange({ fromDate, toDate });
    };

    const exportAsImage = () => {
        const container = containerRef.current;
        if (container) {
            html2canvas(container, { scale: 2 }).then((canvas) => {
                const base64Image = canvas.toDataURL("image/png");
                const link = document.createElement("a");
                link.href = base64Image;
                link.download = "analytics_dashboard.png";
                link.click();
            });
        }
    };

    const exportAsPDF = () => {
        const container = containerRef.current;
        if (container) {
            html2canvas(container, { scale: 2 }).then((canvas) => {
                const base64Image = canvas.toDataURL("image/png");
                const pdf = new jsPDF("landscape");
                pdf.addImage(base64Image, "PNG", 10, 10, 280, 150);
                pdf.save("analytics_dashboard.pdf");
            });
        }
    };

    useEffect(() => {
        if (data) {
            // Extract graph data
            const messageCountByDate: { [date: string]: number } = {};
            data.messages.forEach((message) => {
                const date = new Date(message.createdAt).toLocaleDateString();
                if (messageCountByDate[date]) {
                    messageCountByDate[date]++;
                } else {
                    messageCountByDate[date] = 1;
                }
            });

            // Prepare data for the chart
            const labels = Object.keys(messageCountByDate);
            const messageCounts = Object.values(messageCountByDate);

            setChartData({
                labels: labels,
                datasets: [
                    {
                        label: "Number of Chat Messages",
                        data: messageCounts,
                        borderColor: "#007bff",
                        backgroundColor: "rgba(0, 123, 255, 0.1)",
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: "#007bff",
                        pointBorderColor: "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 6,
                    },
                ],
            });

            // Prepare data for the pie chart
            const conversationCounts: { [key: string]: number } = {};
            data.conversations.forEach((conversation) => {
                const type = conversation.type;
                if (conversationCounts[type]) {
                    conversationCounts[type]++;
                } else {
                    conversationCounts[type] = 1;
                }
            });

            const pieLabels = Object.keys(conversationCounts);
            const pieData = Object.values(conversationCounts);
            setPieChatData({
                labels: pieLabels,
                datasets: [
                    {
                        label: "Conversations by Type",
                        data: pieData,
                        backgroundColor: pieLabels.map(label => 
                            label === "whatsapp" ? "#25D366" : "#007bff"
                        ),
                        borderColor: ["#ffffff", "#ffffff", "#ffffff", "#ffffff"],
                        borderWidth: 2,
                        hoverOffset: 8,
                    },
                ],
            });
        }
    }, [data]);

    if (!agent) return null;

    if (!agent.isPublished) {
        return <PublishAgent agent={agent} />;
    }

    return (
        <div style={{ color: "#ffffff", minHeight: "100vh" }}>
            {/* Header */}
            <div
                style={{ backgroundColor: "#2d2d2d", borderBottom: "1px solid #444" }}
                className="sticky-top"
            >
                <Container fluid className="py-3">
                    <Row className="align-items-center">
                        <Col>
                            <div className="d-flex align-items-center">
                                <div className="bg-primary rounded p-2 me-3">
                                    <FaChartLine className="text-white" />
                                </div>
                                <div>
                                    <h4 className="mb-0 text-light">Analytics Dashboard</h4>
                                    <small style={{ color: "#999" }}>
                                        {dateRange.fromDate.toLocaleDateString()} -{" "}
                                        {dateRange.toDate.toLocaleDateString()}
                                    </small>
                                </div>
                            </div>
                        </Col>
                        <Col xs="auto">
                            <div className="d-flex align-items-center gap-2">
                                <Form.Select
                                    size="sm"
                                    onChange={(e) => handleSelection(e.target.value)}
                                    defaultValue="7days"
                                    style={{
                                        backgroundColor: "#444",
                                        borderColor: "#555",
                                        color: "#fff",
                                        width: "auto",
                                    }}
                                >
                                    <option value="7days">Last 7 Days</option>
                                    <option value="30days">Last 30 Days</option>
                                    <option value="60days">Last 60 Days</option>
                                    <option value="90days">Last 90 Days</option>
                                    <option value="lastCalendarWeek">Last Week</option>
                                    <option value="lastCalendarMonth">Last Month</option>
                                    <option value="thisYear">This Year</option>
                                    <option value="last12months">Last 12 Months</option>
                                    <option value="lastCalendarYear">Last Year</option>
                                </Form.Select>
                                <DropdownButton
                                    variant="outline-light"
                                    size="sm"
                                    title={
                                        <>
                                            <FaDownload className="me-2" />
                                            Export
                                        </>
                                    }
                                >
                                    <Dropdown.Item onClick={exportAsImage}>
                                        <FaFileExport className="me-2" />
                                        Export as Image
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={exportAsPDF}>
                                        <FaFileExport className="me-2" />
                                        Export as PDF
                                    </Dropdown.Item>
                                </DropdownButton>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            <Container fluid className="py-4">
                {isLoading && (
                    <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "400px" }}
                    >
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-light">Loading analytics data...</p>
                        </div>
                    </div>
                )}

                {!isLoading && (
                    <>
                        {/* Stats Cards */}
                        <Row className="mb-4">
                            <Col xs={12} md={3}>
                                <Card
                                    style={{ backgroundColor: "#2d2d2d", borderColor: "#444" }}
                                    className="h-100"
                                >
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <div className="bg-primary rounded-circle p-3 me-3">
                                                <FaUsers className="text-white" size={24} />
                                            </div>
                                            <div>
                                                <h3 className="mb-0 text-light">
                                                    {data?.conversations.length || 0}
                                                </h3>
                                                <small style={{ color: "#999" }}>Total Sessions</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12} md={3}>
                                <Card
                                    style={{ backgroundColor: "#2d2d2d", borderColor: "#444" }}
                                    className="h-100"
                                >
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <div className="bg-success rounded-circle p-3 me-3">
                                                <FaComments className="text-white" size={24} />
                                            </div>
                                            <div>
                                                <h3 className="mb-0 text-light">
                                                    {data?.messages?.length || 0}
                                                </h3>
                                                <small style={{ color: "#999" }}>Total Messages</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12} md={3}>
                                <Card
                                    style={{ backgroundColor: "#2d2d2d", borderColor: "#444" }}
                                    className="h-100"
                                >
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <div className="bg-info rounded-circle p-3 me-3">
                                                <FaGlobe className="text-white" size={24} />
                                            </div>
                                            <div>
                                                <h3 className="mb-0 text-light">
                                                    {data?.conversations.filter((c) => c.type === "web")
                                                        .length || 0}
                                                </h3>
                                                <small style={{ color: "#999" }}>Web Sessions</small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xs={12} md={3}>
                                <Card
                                    style={{ backgroundColor: "#2d2d2d", borderColor: "#444" }}
                                    className="h-100"
                                >
                                    <Card.Body className="text-center">
                                        <div className="d-flex align-items-center justify-content-center mb-2">
                                            <div className="rounded-circle p-3 me-3" style={{ backgroundColor: "#25D366" }}>
                                                <SiWhatsapp className="text-white" size={24} />
                                            </div>
                                            <div>
                                                <h3 className="mb-0 text-light">
                                                    {data?.conversations.filter(
                                                        (c) => c.type === "whatsapp",
                                                    ).length || 0}
                                                </h3>
                                                <small style={{ color: "#999" }}>
                                                    WhatsApp Sessions
                                                </small>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Charts Section */}
                        <Row>
                            {/* Line Chart */}
                            <Col xs={12} lg={8}>
                                <Card
                                    style={{ backgroundColor: "#2d2d2d", borderColor: "#444", height: "550px" }}
                                    className="mb-4"
                                >
                                    <Card.Body className="d-flex flex-column">
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <h5 className="mb-0 text-light">
                                                <FaChartLine className="me-2" />
                                                Message Activity Over Time
                                            </h5>
                                            <Badge bg="primary">
                                                {data?.messages?.length || 0} total messages
                                            </Badge>
                                        </div>
                                        <div ref={containerRef} style={{ height: "450px", flex: 1 }}>
                                            {chartData && (
                                                <Line
                                                    ref={chartRef}
                                                    data={chartData}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: {
                                                            legend: {
                                                                labels: {
                                                                    color: "#ffffff",
                                                                },
                                                            },
                                                        },
                                                        scales: {
                                                            x: {
                                                                title: {
                                                                    display: true,
                                                                    text: "Date",
                                                                    color: "#ffffff",
                                                                },
                                                                ticks: {
                                                                    color: "#ffffff",
                                                                },
                                                                grid: {
                                                                    color: "#444",
                                                                },
                                                            },
                                                            y: {
                                                                title: {
                                                                    display: true,
                                                                    text: "Number of Messages",
                                                                    color: "#ffffff",
                                                                },
                                                                beginAtZero: true,
                                                                ticks: {
                                                                    color: "#ffffff",
                                                                },
                                                                grid: {
                                                                    color: "#444",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Pie Chart */}
                            <Col xs={12} lg={4}>
                                <Card
                                    style={{ backgroundColor: "#2d2d2d", borderColor: "#444", height: "550px" }}
                                    className="mb-4"
                                >
                                    <Card.Body className="d-flex flex-column">
                                        <h5 className="mb-3 text-light">Conversations by Source</h5>
                                        <div style={{ height: "450px", flex: 1 }}>
                                            {pieChatData && (
                                                <Pie
                                                    data={pieChatData}
                                                    options={{
                                                        responsive: true,
                                                        maintainAspectRatio: false,
                                                        plugins: {
                                                            legend: {
                                                                position: "bottom" as const,
                                                                labels: {
                                                                    color: "#ffffff",
                                                                    padding: 20,
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Detailed Stats */}
                        <Row>
                            <Col xs={12}>
                                <Card
                                    style={{ backgroundColor: "#2d2d2d", borderColor: "#444" }}
                                >
                                    <Card.Body>
                                        <h5 className="mb-3 text-light">
                                            <FaCalendarAlt className="me-2" />
                                            Detailed Analytics Summary
                                        </h5>
                                        <Row>
                                            <Col md={6}>
                                                <div style={{ lineHeight: 2.5 }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span style={{ color: "#ccc" }}>
                                                            Total Sessions:
                                                        </span>
                                                        <Badge bg="primary" className="px-3 py-2">
                                                            {data?.conversations.length || 0}
                                                        </Badge>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span style={{ color: "#ccc" }}>
                                                            Total Messages:
                                                        </span>
                                                        <Badge bg="success" className="px-3 py-2">
                                                            {data?.messages?.length || 0}
                                                        </Badge>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span style={{ color: "#ccc" }}>Web Sessions:</span>
                                                        <Badge bg="info" className="px-3 py-2">
                                                            {data?.conversations.filter(
                                                                (c) => c.type === "web",
                                                            ).length || 0}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div style={{ lineHeight: 2.5 }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span style={{ color: "#ccc" }}>
                                                            WhatsApp Sessions:
                                                        </span>
                                                        <Badge style={{ backgroundColor: "#25D366" }} className="px-3 py-2">
                                                            {data?.conversations.filter(
                                                                (c) => c.type === "whatsapp",
                                                            ).length || 0}
                                                        </Badge>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span style={{ color: "#ccc" }}>
                                                            Avg Messages per Session:
                                                        </span>
                                                        <Badge bg="secondary" className="px-3 py-2">
                                                            {data?.conversations.length
                                                                ? Math.round(
                                                                        (data?.messages?.length || 0) /
                                                                            data.conversations.length,
                                                                    )
                                                                : 0}
                                                        </Badge>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span style={{ color: "#ccc" }}>Date Range:</span>
                                                        <small style={{ color: "#999" }}>
                                                            {dateRange.fromDate.toLocaleDateString()} -{" "}
                                                            {dateRange.toDate.toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        </div>
    );
}

export default MarketingDashboard;
